import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Alertmessage } from "../../widgets";
import {
  FaBars,
  FaRegCalendarAlt,
  FaTimes,
  FaExclamationCircle,
  FaPlus,
  FaHistory,
  FaUser,
  FaBuilding,
} from "react-icons/fa";
import "./Pjc_dashboard.css";

axios.defaults.withCredentials = true;

const USER_NAVIGATION = [
  { label: "Contribution Schedule", icon: FaRegCalendarAlt },
  { label: "Transaction History", icon: FaHistory },
];

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value || 0);

// Validation Regex Patterns
const AADHAR_REGEX = /^\d{12}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PjcDashboard = () => {
  const navigate = useNavigate();

  // Authentication & Session State (strictly API based)
  const [authStatus, setAuthStatus] = useState("loading"); // "loading" | "authenticated" | "unauthenticated"
  const [currentUser, setCurrentUser] = useState(null);

  // UI Navigation State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Contribution Schedule");

  // Data Loading & Error States
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState("");
  const [alertMsg, setAlertMsg] = useState("");

  // Alumni Multi-Plan State
  const [userPlans, setUserPlans] = useState([]);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [userTransactions, setUserTransactions] = useState([]);

  // Pay Now Modal State
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [payFormData, setPayFormData] = useState({
    transaction_reference: "",
    amount_paid: "",
    payment_mode: "UPI",
    upi_id: "",
    bank_name: "",
    cheque_no: "",
    payment_date: new Date().toISOString().split("T")[0],
    remarks: "",
  });

  // Create Payment Plan Modal State
  const [createPlanModalOpen, setCreatePlanModalOpen] = useState(false);
  const [isSubmittingPlan, setIsSubmittingPlan] = useState(false);
  const [planErrors, setPlanErrors] = useState({});
  const [planFormData, setPlanFormData] = useState({
    fundType: "OWN",
    donorName: "",
    aadharNo: "",
    panNo: "",
    companyName: "",
    contactPerson: "",
    contactPersonEmail: "",
    totalAmount: "",
    installmentCount: 1,
    installments: [
      {
        due_amount: "",
        due_date: "",
      },
    ],
  });

  // Active Selected Plan
  const activePlan = useMemo(() => {
    return userPlans[selectedPlanIndex] || null;
  }, [userPlans, selectedPlanIndex]);

  // Verify Session on Mount
  useEffect(() => {
    let isMounted = true;

    const verifySession = async () => {
      try {
        const res = await axios.get("/backend/alumni/login_verification");
        if (isMounted && res.data && res.data.token_status === "okay") {
          setCurrentUser(res.data.user || null);
          setActiveItem("Contribution Schedule");
          setAuthStatus("authenticated");
          return;
        }
      } catch (err) {
        console.log("Session verification failed:", err?.message);
      }

      if (isMounted) {
        setAuthStatus("unauthenticated");
      }
    };

    verifySession();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch Dashboard Data when Authenticated
  useEffect(() => {
    if (authStatus !== "authenticated") return;
    fetchAlumniData();
  }, [authStatus]);

  const fetchAlumniData = async () => {
    setLoadingData(true);
    setDataError("");
    try {
      const [planRes, txRes] = await Promise.allSettled([
        axios.get("/backend/payment-plans/mine"),
        axios.get("/backend/transactions/mine"),
      ]);

      if (planRes.status === "fulfilled" && planRes.value.data?.plans) {
        const plans = planRes.value.data.plans || [];
        setUserPlans(plans);
        setSelectedPlanIndex(0);
      } else {
        setUserPlans([]);
      }

      if (txRes.status === "fulfilled" && txRes.value.data?.transactions) {
        setUserTransactions(txRes.value.data.transactions);
      } else {
        setUserTransactions([]);
      }
    } catch (err) {
      console.error("Error fetching alumni records:", err);
      setDataError("Failed to fetch payment plans from server.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post("/backend/alumni/logout");
    } catch { }
    setAuthStatus("unauthenticated");
    navigate("/alumni-login", { replace: true, state: { loggedOut: true } });
  };

  /* Open Create Payment Plan Modal */
  const handleOpenCreatePlanModal = () => {
    setPlanErrors({});
    setPlanFormData({
      fundType: "OWN",
      donorName: currentUser?.fullname || "",
      aadharNo: "",
      panNo: "",
      companyName: "",
      contactPerson: "",
      contactPersonEmail: "",
      totalAmount: "",
      installmentCount: 1,
      installments: [
        {
          due_amount: "",
          due_date: "",
        },
      ],
    });
    setCreatePlanModalOpen(true);
  };

  /* Helper: evenly split a total amount across N installments.
     Any remainder (from integer division) is added to the last
     installment so the sum always matches the total exactly. */
  const splitAmountAcrossInstallments = (total, count) => {
    const safeTotal = Number(total) || 0;
    const safeCount = Number(count) || 1;
    if (!safeTotal) return Array.from({ length: safeCount }, () => "");

    const base = Math.floor(safeTotal / safeCount);
    const remainder = safeTotal - base * safeCount;

    return Array.from({ length: safeCount }, (_, idx) =>
      String(base + (idx === safeCount - 1 ? remainder : 0))
    );
  };

  /* Form Handlers for Create Payment Plan */
  const handlePlanFormChange = (e) => {
    let { name, value } = e.target;

    if (name === "aadharNo") {
      value = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "panNo") {
      value = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10);
    } else if (name === "totalAmount") {
      value = value.replace(/\D/g, "");
    }

    setPlanFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Auto-split the total pledge amount evenly across all installments
      // whenever the total amount changes (works for any installment count).
      if (name === "totalAmount") {
        const count = prev.installmentCount || 1;
        const splitAmounts = splitAmountAcrossInstallments(value, count);

        updated.installments = Array.from({ length: count }, (_, idx) => ({
          due_amount: splitAmounts[idx],
          due_date: prev.installments[idx]?.due_date || "",
        }));
      }

      return updated;
    });

    if (planErrors[name] || planErrors.emiSum) {
      setPlanErrors((prev) => ({ ...prev, [name]: "", emiSum: "" }));
    }
  };

  const handleInstallmentCountChange = (e) => {
    const count = Number(e.target.value);

    setPlanFormData((prev) => {
      const currentEMI = prev.installments || [];

      // Auto-split the existing total pledge amount evenly across the
      // new number of installments.
      const splitAmounts = splitAmountAcrossInstallments(prev.totalAmount, count);

      const newSchedule = Array.from({ length: count }, (_, idx) => ({
        due_amount: prev.totalAmount ? splitAmounts[idx] : currentEMI[idx]?.due_amount || "",
        due_date: currentEMI[idx]?.due_date || "",
      }));

      return {
        ...prev,
        installmentCount: count,
        installments: newSchedule,
      };
    });

    setPlanErrors((prev) => ({ ...prev, emiSum: "", installments: [] }));
  };

  const handleEMIChange = (index, field, value) => {
    if (field === "due_amount") {
      value = value.replace(/\D/g, "");
    }

    setPlanFormData((prev) => {
      const updatedEMI = [...prev.installments];
      updatedEMI[index] = { ...updatedEMI[index], [field]: value };
      return { ...prev, installments: updatedEMI };
    });

    if (planErrors.emiSum || (planErrors.installments && planErrors.installments[index])) {
      setPlanErrors((prev) => {
        const updatedEmiErrors = [...(prev.installments || [])];
        if (updatedEmiErrors[index]) {
          updatedEmiErrors[index] = { ...updatedEmiErrors[index], [field]: "" };
        }
        return { ...prev, emiSum: "", installments: updatedEmiErrors };
      });
    }
  };

  const validatePlanForm = () => {
    const errors = {};

    if (!planFormData.totalAmount || Number(planFormData.totalAmount) <= 0) {
      errors.totalAmount = "Please enter a valid total pledge amount.";
    }

    if (planFormData.fundType === "OWN") {
      if (!planFormData.donorName?.trim()) {
        errors.donorName = "Donor Name is required.";
      }
      if (!planFormData.aadharNo || !AADHAR_REGEX.test(planFormData.aadharNo)) {
        errors.aadharNo = "A valid 12-digit Aadhar is required.";
      }
      if (planFormData.panNo && !PAN_REGEX.test(planFormData.panNo)) {
        errors.panNo = "Invalid PAN number format.";
      }
    } else {
      if (!planFormData.companyName?.trim()) {
        errors.companyName = "Company Name is required.";
      }
      if (!planFormData.contactPerson?.trim()) {
        errors.contactPerson = "Contact Person name is required.";
      }
      if (!planFormData.contactPersonEmail || !EMAIL_REGEX.test(planFormData.contactPersonEmail)) {
        errors.contactPersonEmail = "A valid contact email is required.";
      }
      if (planFormData.panNo && !PAN_REGEX.test(planFormData.panNo)) {
        errors.panNo = "Invalid company PAN number format.";
      }
    }

    // Verify installments
    const instErrors = [];
    let sumOfEmis = 0;
    planFormData.installments.forEach((emi, idx) => {
      const emiErr = {};
      if (!emi.due_amount || Number(emi.due_amount) <= 0) {
        emiErr.due_amount = "Amount required.";
      } else {
        sumOfEmis += Number(emi.due_amount);
      }
      if (!emi.due_date) {
        emiErr.due_date = "Date required.";
      }
      instErrors[idx] = emiErr;
    });

    const hasInstErrors = instErrors.some((e) => Object.keys(e).length > 0);
    if (hasInstErrors) {
      errors.installments = instErrors;
    }
    
    

    const targetTotal = Number(planFormData.totalAmount) || 0;
    if (!errors.totalAmount && sumOfEmis !== targetTotal) {
      errors.emiSum = `Sum of installments (₹${sumOfEmis}) does not match your total pledge amount (₹${targetTotal})`;
    }

    setPlanErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreatePlanSubmit = async (e) => {
    e.preventDefault();

    if (!validatePlanForm()) return;

    setIsSubmittingPlan(true);

    const payload = {
      fund_type: planFormData.fundType,
      donor_name: planFormData.fundType === "OWN" ? planFormData.donorName.trim() : null,
      aadhar_no: planFormData.fundType === "OWN" ? planFormData.aadharNo.trim() : null,
      pan_no: planFormData.panNo ? planFormData.panNo.trim() : null,
      company_name: planFormData.fundType === "CSR" ? planFormData.companyName.trim() : null,
      contact_person: planFormData.fundType === "CSR" ? planFormData.contactPerson.trim() : null,
      contact_person_email: planFormData.fundType === "CSR" ? planFormData.contactPersonEmail.trim() : null,
      total_amount: Number(planFormData.totalAmount),
      no_of_installments: Number(planFormData.installmentCount),
      installments: planFormData.installments.map((emi, idx) => ({
        installment_no: idx + 1,
        due_amount: Number(emi.due_amount),
        due_date: emi.due_date,
      })),
    };

    try {
      const res = await axios.post("/backend/payment-plans", payload);
      if (res.status === 200 || res.status === 201) {
        setAlertMsg("Pledge payment plan created successfully!");
        setCreatePlanModalOpen(false);
        fetchAlumniData();
      }
    } catch (err) {
      console.error(err);
      setAlertMsg(err.response?.data?.error || "Failed to create payment plan.");
    } finally {
      setIsSubmittingPlan(false);
    }
  };

  /* Submit Payment Flow */
  const handleOpenPayModal = (inst) => {
    setSelectedInstallment(inst);
    setPayFormData({
      transaction_reference: "",
      amount_paid: String(inst.remaining_amount !== undefined ? inst.remaining_amount : inst.due_amount),
      payment_mode: "UPI",
      upi_id: "",
      bank_name: "",
      cheque_no: "",
      payment_date: new Date().toISOString().split("T")[0],
      remarks: "",
    });
    setPayModalOpen(true);
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();

    if (!selectedInstallment) return;

    setIsSubmittingPayment(true);

    const payload = {
      installment_id: selectedInstallment.id,
      transaction_reference: payFormData.transaction_reference.trim(),
      amount_paid: Number(payFormData.amount_paid),
      payment_mode: payFormData.payment_mode,
      upi_id: payFormData.payment_mode === "UPI" ? payFormData.upi_id.trim() : null,
      bank_name: payFormData.payment_mode === "Net Banking" ? payFormData.bank_name.trim() : null,
      cheque_no: payFormData.payment_mode === "Cheque" ? payFormData.cheque_no.trim() : null,
      payment_date: payFormData.payment_date,
      remarks: payFormData.remarks || null,
    };

    try {
      await axios.post("/backend/transactions", payload);
      setAlertMsg("Payment submitted successfully! Pending admin verification.");
      setPayModalOpen(false);
      fetchAlumniData();
    } catch (err) {
      console.error("Payment submission failed:", err);
      setAlertMsg(err.response?.data?.error || "Failed to submit transaction. Please try again.");
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  /* Calculated Metrics for Selected Plan */
  const planTotals = useMemo(() => {
    const totalPledged = activePlan?.total_amount || 0;
    const installments = activePlan?.installments || [];

    const totalPaid = installments
      .filter((i) => i.status === "Approved" || i.status === "Paid" || Number(i.paid_amount) > 0)
      .reduce((sum, i) => sum + Number(i.paid_amount || i.due_amount || 0), 0);

    const pendingInstallment = installments.find(
      (i) => i.status === "Pending" || i.status === "Due" || i.status === "Rejected" || !i.status
    );

    const remainingBalance = Math.max(0, totalPledged - totalPaid);

    return {
      pledgeType: activePlan?.fund_type || "OWN",
      totalPledged,
      totalPaid,
      remainingBalance,
      nextDue: pendingInstallment && pendingInstallment.due_date
        ? String(pendingInstallment.due_date).split("T")[0]
        : "All Cleared",
    };
  }, [activePlan]);

  if (authStatus === "loading") {
    return (
      <div className="pjc-loading-screen">
        <div className="pjc-spinner" />
        <p>Loading Alumni Fund System...</p>
      </div>
    );
  }

  if (authStatus === "unauthenticated") {
    return <Navigate to="/alumni-login" replace />;
  }

  return (
    <div className="pjc-dashboard-page">
      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <button
          className="pjc-sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close navigation sidebar"
        />
      )}

      {/* Redesigned Sidebar Navigation */}
      <aside className={`pjc-sidebar ${mobileOpen ? "is-open" : ""}`}>
        <div className="pjc-brand">
          <div className="pjc-brand-text">
            <span>PJC Fund</span>
            <small>Alumni View</small>
          </div>
          <button className="pjc-close-sidebar" onClick={() => setMobileOpen(false)} aria-label="Close navigation">
            <FaTimes />
          </button>
        </div>

        <div className="pjc-sidebar-action-wrap">
          <button
            className="pjc-create-plan-sidebar-btn"
            onClick={handleOpenCreatePlanModal}
            title="Create a new payment plan pledge"
          >
            <FaPlus />
            <span>Create Payment Plan</span>
          </button>
        </div>

        <nav className="pjc-nav" aria-label="Dashboard navigation">
          {USER_NAVIGATION.map(({ label, icon: Icon }) => (
            <button
              key={label}
              className={`pjc-nav-link ${activeItem === label ? "active" : ""}`}
              onClick={() => {
                setActiveItem(label);
                setMobileOpen(false);
              }}
              title={label}
            >
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Workspace Area */}
      <main className="pjc-dashboard-content">
        {/* Top Header Bar */}
        <header className="pjc-topbar">
          <div className="pjc-topbar-title-group">

            <button className="pjc-menu-button" onClick={() => setMobileOpen(true)} aria-label="Open sidebar menu">
              <FaBars />
            </button>
            <div className="pjc-brand-logo-wrapper">
  <img
    src="https://accet-site-media-trial.s3.ap-northeast-1.amazonaws.com/Pjc+module/pjc+logo+(1).webp"
    alt="PJC Fund Logo"
    className="pjc-brand-logo"
  />
</div>
            <div>
              <h1>Welcome, {currentUser?.fullname?.split(" ")[0] || "Alumni"}</h1>
            </div>
          </div>

          <div className="pjc-topbar-actions">
            <button className="pjc-logout-btn" onClick={handleLogout} title="Logout of session">
              Logout
            </button>
          </div>
        </header>

        {/* Dynamic Main Workspace Container */}
        <div className="pjc-dashboard-body">
          {dataError && (
            <div className="pjc-alert warning">
              <FaExclamationCircle /> {dataError}
            </div>
          )}

          <section className="pjc-dashboard-section">
            {/* Multiple Payment Plans Selector */}
            {userPlans.length > 0 && (
              <div className="pjc-plan-selector-container">
                <div className="pjc-plan-selector-header">
                  <h3>My Payment Plans</h3>
                  <button className="pjc-action-btn secondary" onClick={handleOpenCreatePlanModal}>
                    <FaPlus /> New Plan
                  </button>
                </div>
                <div className="pjc-plan-cards-grid">
                  {userPlans.map((plan, idx) => (
                    <div
                      key={plan.id || idx}
                      className={`pjc-plan-card ${selectedPlanIndex === idx ? "selected" : ""}`}
                      onClick={() => setSelectedPlanIndex(idx)}
                    >
                      <h4 className="pjc-plan-donor">{plan.donor_name || "Alumni Donor"}</h4>
                      <div className="pjc-plan-card-footer">
                        <div className="pjc-plan-amount">{formatMoney(plan.total_amount)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Minimal Summary Metrics Cards for Active Plan */}
            {userPlans.length > 0 ? (
              <div className="pjc-summary-grid pjc-user-summary-grid">
                <article className="pjc-summary-card">
                  <div>
                    <p>Pledge Type</p>
                    <h3>{planTotals.pledgeType}</h3>
                  </div>
                </article>

                <article className="pjc-summary-card">
                  <div>
                    <p>Total Pledged</p>
                    <h3>{formatMoney(planTotals.totalPledged)}</h3>
                  </div>
                </article>

                <article className="pjc-summary-card">
                  <div>
                    <p>Total Paid</p>
                    <h3>{formatMoney(planTotals.totalPaid)}</h3>
                  </div>
                </article>

                <article className="pjc-summary-card">
                  <div>
                    <p>Next Installment Due</p>
                    <h3 style={{ fontSize: "14px" }}>Balance: {formatMoney(planTotals.remainingBalance)}</h3>
                    <small>{planTotals.nextDue}</small>
                  </div>
                </article>
              </div>
            ) : null}

            {/* Contribution Schedule Table */}
            {activeItem === "Contribution Schedule" && (
              <section className="pjc-section-wrap">
                <h2 className="pjc-section-title">Installments Plan & Payments</h2>
                <div className="pjc-panel">
                  <div className="pjc-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Inst. No</th>
                          <th>Due Amount</th>
                          <th>Due Date</th>
                          <th>Paid Amount</th>
                          <th>Paid Date</th>
                          <th>Receipt Status</th>
                          <th>Payment Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingData ? (
                          <tr>
                            <td colSpan={7} className="pjc-empty-cell">
                              Loading installments...
                            </td>
                          </tr>
                        ) : !activePlan || !activePlan.installments || activePlan.installments.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="pjc-empty-cell">
                              No active installments found. Make a contribution pledge plan to start!
                            </td>
                          </tr>
                        ) : (
                          activePlan.installments.map((inst, idx) => (
                            <tr key={inst.id || idx}>
                              <td>
                                <strong> {inst.installment_no}</strong>
                              </td>
                              <td>{formatMoney(inst.due_amount)}</td>
                              <td>{String(inst.due_date).split("T")[0]}</td>
                              <td>{inst.paid_amount ? formatMoney(inst.paid_amount) : "—"}</td>
                              <td>{inst.payment_date ? String(inst.payment_date).split("T")[0] : "—"}</td>
                              <td>
                                <span className={`pjc-status ${(inst.status || "due").toLowerCase()}`}>
                                  {inst.status || "DUE"}
                                </span>
                              </td>
                              <td>
                                {!inst.status || inst.status === "Due" || inst.status === "Rejected" || inst.status === "Partial" ? (
                                  <button
                                    className="pjc-pay-button table-btn"
                                    onClick={() => handleOpenPayModal(inst)}
                                    title="Submit transaction proof to pay this installment"
                                  >
                                    Pay Now
                                  </button>
                                ) : inst.status === "Pending" ? (
                                  <span style={{ fontSize: "12px", color: "#f59e0b", fontWeight: "600" }}>
                                    Awaiting Admin Verification
                                  </span>
                                ) : (
                                  <span style={{ fontSize: "12px", color: "#10b981", fontWeight: "600" }}>
                                    Fully Credited
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* Transaction History Table */}
            {activeItem === "Transaction History" && (
              <section className="pjc-section-wrap">
                <h2 className="pjc-section-title">Transaction History</h2>
                <div className="pjc-panel">
                  <div className="pjc-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date Uploaded</th>
                          <th>Reference </th>
                          <th>Installment</th>
                          <th>Amount Paid</th>
                          <th>Payment Method</th>
                          <th>Verification Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingData ? (
                          <tr>
                            <td colSpan={6} className="pjc-empty-cell">
                              Loading transaction receipt logs...
                            </td>
                          </tr>
                        ) : userTransactions.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="pjc-empty-cell">
                              No payment transactions submitted yet.
                            </td>
                          </tr>
                        ) : (
                          userTransactions.map((tx) => (
                            <tr key={tx.id || tx.reference}>
                              <td>{tx.date || tx.payment_date ? String(tx.date || tx.payment_date).split("T")[0] : "-"}</td>
                              <td className="pjc-reference">{tx.reference || tx.transaction_reference || `TXN-${tx.id}`}</td>
                              <td>{tx.installment_no || "-"}</td>
                              <td>{formatMoney(tx.amount || tx.amount_paid)}</td>
                              <td>{tx.method || tx.payment_mode}</td>
                              <td>
                                <span className={`pjc-status ${(tx.approval_status || "pending").toLowerCase().replace(/\s+/g, "-")}`}>
                                  {tx.approval_status || "PENDING"}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}
          </section>
        </div>
      </main>

      {/* CREATE PAYMENT PLAN MODAL */}
      {createPlanModalOpen && (
        <div className="pjc-modal-overlay">
          <div className="pjc-modal pjc-large-modal">
            <div className="pjc-modal-header">
              <h3>
                <FaPlus style={{ fontSize: "14px", color: "#e8720c" }} /> Create New Payment Plan
              </h3>
              <button className="pjc-modal-close" onClick={() => setCreatePlanModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreatePlanSubmit} noValidate>
              <div className="pjc-modal-body">
                {planErrors.emiSum && (
                  <div className="pjc-alert warning" style={{ marginBottom: "16px" }}>
                    <FaExclamationCircle /> {planErrors.emiSum}
                  </div>
                )}

                {/* Fund Type Radio Selection */}
                <div className="pjc-form-group">
                  <label>
                    Fund Type <span className="req">*</span>
                  </label>
                  <div className="pjc-radio-group">
                    <label className={`pjc-radio-card ${planFormData.fundType === "OWN" ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="fundType"
                        value="OWN"
                        checked={planFormData.fundType === "OWN"}
                        onChange={handlePlanFormChange}
                      />
                      <FaUser />
                      <div>
                        <strong>Own Contribution</strong>
                        <small>Individual alumni pledge</small>
                      </div>
                    </label>

                    <label className={`pjc-radio-card ${planFormData.fundType === "CSR" ? "selected" : ""}`}>
                      <input
                        type="radio"
                        name="fundType"
                        value="CSR"
                        checked={planFormData.fundType === "CSR"}
                        onChange={handlePlanFormChange}
                      />
                      <FaBuilding />
                      <div>
                        <strong>CSR Contribution</strong>
                        <small>Corporate Social Responsibility</small>
                      </div>
                    </label>
                  </div>
                </div>

                {/* OWN Fund Fields */}
                {planFormData.fundType === "OWN" && (
                  <div className="pjc-form-section">
                    <h4>Personal & Tax Information</h4>
                    <div className="pjc-form-group">
                      <label>
                        Donor Name <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        name="donorName"
                        placeholder="Full Donor Name"
                        value={planFormData.donorName}
                        onChange={handlePlanFormChange}
                        required
                        readOnly
                      />
                      {planErrors.donorName && <span className="pjc-field-error">{planErrors.donorName}</span>}
                    </div>

                    <div className="pjc-form-grid">
                      <div className="pjc-form-group">
                        <label>
                          Aadhar Number <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          name="aadharNo"
                          placeholder="12-digit Aadhar"
                          value={planFormData.aadharNo}
                          onChange={handlePlanFormChange}
                          required
                        />
                        {planErrors.aadharNo && <span className="pjc-field-error">{planErrors.aadharNo}</span>}
                      </div>

                      <div className="pjc-form-group">
                        <label>PAN Number</label>
                        <input
                          type="text"
                          name="panNo"
                          placeholder="e.g. ABCDE1234F"
                          value={planFormData.panNo}
                          onChange={handlePlanFormChange}
                        />
                        {planErrors.panNo && <span className="pjc-field-error">{planErrors.panNo}</span>}
                      </div>
                    </div>
                  </div>
                )}

                {/* CSR Fund Fields */}
                {planFormData.fundType === "CSR" && (
                  <div className="pjc-form-section">
                    <h4>Corporate & Contact Information</h4>
                    <div className="pjc-form-grid">
                      <div className="pjc-form-group">
                        <label>
                          Company Name <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          placeholder="Enter Registered Company Name"
                          value={planFormData.companyName}
                          onChange={handlePlanFormChange}
                          required
                        />
                        {planErrors.companyName && <span className="pjc-field-error">{planErrors.companyName}</span>}
                      </div>

                      <div className="pjc-form-group">
                        <label>Company PAN Number</label>
                        <input
                          type="text"
                          name="panNo"
                          placeholder="Company PAN"
                          value={planFormData.panNo}
                          onChange={handlePlanFormChange}
                        />
                        {planErrors.panNo && <span className="pjc-field-error">{planErrors.panNo}</span>}
                      </div>
                    </div>

                    <div className="pjc-form-grid">
                      <div className="pjc-form-group">
                        <label>
                          Contact Person <span className="req">*</span>
                        </label>
                        <input
                          type="text"
                          name="contactPerson"
                          placeholder="Name of Representative"
                          value={planFormData.contactPerson}
                          onChange={handlePlanFormChange}
                          required
                        />
                        {planErrors.contactPerson && <span className="pjc-field-error">{planErrors.contactPerson}</span>}
                      </div>

                      <div className="pjc-form-group">
                        <label>
                          Contact Person Email <span className="req">*</span>
                        </label>
                        <input
                          type="email"
                          name="contactPersonEmail"
                          placeholder="representative@domain.com"
                          value={planFormData.contactPersonEmail}
                          onChange={handlePlanFormChange}
                          required
                        />
                        {planErrors.contactPersonEmail && (
                          <span className="pjc-field-error">{planErrors.contactPersonEmail}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Total pledge amount and installment count input */}
                <div className="pjc-form-section">
                  <h4>Pledge & Installments Setup</h4>
                  <div className="pjc-form-grid">
                    <div className="pjc-form-group">
                      <label>
                        Total Pledge Amount (₹) <span className="req">*</span>
                      </label>
                      <input
                        type="text"
                        name="totalAmount"
                        placeholder="e.g. 50000"
                        value={planFormData.totalAmount}
                        onChange={handlePlanFormChange}
                        required
                      />
                      {planErrors.totalAmount && <span className="pjc-field-error">{planErrors.totalAmount}</span>}
                    </div>

                    <div className="pjc-form-group">
                      <label>Number of Installments</label>
                      <select value={planFormData.installmentCount} onChange={handleInstallmentCountChange}>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>
                            {n} {n === 1 ? "Installment" : "Installments"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="pjc-form-group">
                    <label style={{ fontSize: "12.5px", fontWeight: "700", color: "#0b1f4d", marginBottom: "8px", display: "block" }}>
                      Installment Breakdown Dates & Amounts
                    </label>

                    {planFormData.installments.map((inst, idx) => (
                      <div key={idx} className="pjc-emi-row">
                        <span className="pjc-emi-label">Inst {idx + 1}</span>
                        <div className="pjc-emi-inputs">
                          <div className="pjc-emi-inputs">
                            <input
                              type="date"
                              value={inst.due_date ||""}
                           min={
  idx === 0
    ? new Date().toISOString().split("T")[0]
    : planFormData.installments[idx - 1]?.due_date
      ? (() => {
          const d = new Date(planFormData.installments[idx - 1].due_date);
          d.setDate(d.getDate() + 1);
          return d.toISOString().split("T")[0];
        })()
      : undefined
}
                              onChange={(e) => handleEMIChange(idx, "due_date", e.target.value)}
                              required
                            />

                            <input
                              type="text"
                              placeholder="Amount (₹)"
                              value={inst.due_amount}
                              onChange={(e) =>
                                handleEMIChange(
                                  idx,
                                  "due_amount",
                                  e.target.value.replace(/\D/g, "")
                                )
                              }
                              required
                            />
                          </div>
                        </div>
                        {planErrors.installments?.[idx]?.due_amount && (
                          <span className="pjc-field-error">{planErrors.installments[idx].due_amount}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pjc-modal-footer">
                <button
                  className="pjc-action-btn secondary"
                  type="button"
                  onClick={() => setCreatePlanModalOpen(false)}
                >
                  Cancel
                </button>
                <button className="pjc-pay-button" type="submit" disabled={isSubmittingPlan}>
                  {isSubmittingPlan ? "Saving Plan..." : "Create Payment Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pay Now Modal Form */}
      {payModalOpen && selectedInstallment && (
        <div className="pjc-modal-overlay">
          <div className="pjc-modal">
            <div className="pjc-modal-header">
              <h3>Submit Payment – Installment #{selectedInstallment.installment_no}</h3>
              <button className="pjc-modal-close" onClick={() => setPayModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handlePaySubmit}>
              <div className="pjc-modal-body">
                <div className="pjc-modal-banner">
                  <span>Due Amount:</span>
                  <strong>{formatMoney(selectedInstallment.due_amount)}</strong>
                </div>

                <div className="pjc-form-group">
                  <label>
                    Transaction Reference Number <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Transaction Reference / UTR Number"
                    value={payFormData.transaction_reference}
                    onChange={(e) => setPayFormData({ ...payFormData, transaction_reference: e.target.value })}
                    required
                  />
                </div>

                <div className="pjc-form-grid">
                  <div className="pjc-form-group">
                    <label>
                      Payment Amount (₹) <span className="req">*</span>
                    </label>
                    <input
                      type="number"
                      value={payFormData.amount_paid}
                      onChange={(e) => setPayFormData({ ...payFormData, amount_paid: e.target.value })}
                      required
                    />
                  </div>
                  <div className="pjc-form-group">
                    <label>
                      Payment Method <span className="req">*</span>
                    </label>
                    <select
                      value={payFormData.payment_mode}
                      onChange={(e) => setPayFormData({ ...payFormData, payment_mode: e.target.value })}
                    >
                      <option value="UPI">UPI (GPay / PhonePe / Paytm)</option>
                      <option value="Net Banking">Net Banking</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                </div>

                {payFormData.payment_mode === "UPI" && (
                  <div className="pjc-form-group">
                    <label>
                      UPI ID <span className="req">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="username@upi"
                      value={payFormData.upi_id}
                      onChange={(e) => setPayFormData({ ...payFormData, upi_id: e.target.value })}
                      required
                    />
                  </div>
                )}

                {payFormData.payment_mode === "Net Banking" && (
                  <div className="pjc-form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      placeholder="State Bank of India, etc."
                      value={payFormData.bank_name}
                      onChange={(e) => setPayFormData({ ...payFormData, bank_name: e.target.value })}
                    />
                  </div>
                )}

                <div className="pjc-form-group">
                  <label>
                    Transaction Date <span className="req">*</span>
                  </label>
                  <input
                    type="date"
                    value={payFormData.payment_date}
                    onChange={(e) => setPayFormData({ ...payFormData, payment_date: e.target.value })}
                    required
                  />
                </div>

                <div className="pjc-form-group">
                  <label>Remarks (Optional)</label>
                  <textarea
                    placeholder="Any notes for Admin verification..."
                    value={payFormData.remarks}
                    onChange={(e) => setPayFormData({ ...payFormData, remarks: e.target.value })}
                  />
                </div>
              </div>
              <div className="pjc-modal-footer">
                <button className="pjc-action-btn secondary" type="button" onClick={() => setPayModalOpen(false)}>
                  Cancel
                </button>
                <button className="pjc-pay-button" type="submit" disabled={isSubmittingPayment}>
                  {isSubmittingPayment ? "Submitting..." : "Submit Payment for Verification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Alertmessage message={alertMsg} onClose={() => setAlertMsg("")} />
    </div>
  );
};

export default PjcDashboard;